// Q9 S6 — Per-section client-side validators (D9 advance gate +
// D7 cross-section gate's client mirror).
//
// Spec source: docs/specs/visit-report-per-section-validation.md
//   §Per-section advance gate (`canAdvance(step, formState)`).
//
// TASK-48 S2 — apiReasonField is now typed as `keyof SubmitInput`
// (compile-time enforcement). Renaming a schema field will fail tsc
// at every literal call-site here. The pure-JS validator pattern is
// retained per Bb (orthogonal to the field-name drift bug class — the
// validators encode rule logic, not schema shape).

import type { SubmitInput } from "@hcm/core";
import type { FormState } from "./visit-report-types.js";
import type { SectionStep } from "./visit-report-conditional-rules.js";

/** Map of API field name → ordered error messages. Empty map = section ok. */
export type SectionFieldErrors = ReadonlyMap<string, readonly string[]>;

/** Convenience accessor — first error message for a field, or undefined.
 *  Sections pass this to FormField/SelectField as the `error` prop. */
export function fieldErrorMessage(
  map: SectionFieldErrors | undefined, apiField: string,
): string | undefined {
  return map?.get(apiField)?.[0];
}

export interface SectionValidationResult {
  /** True iff no per-field error AND no cross-field gate failed. */
  isComplete: boolean;
  fieldErrors: SectionFieldErrors;
  /** Section-level errors that don't anchor to a specific field. */
  formErrors:  readonly string[];
}

const EMPTY_RESULT: SectionValidationResult = {
  isComplete: true,
  fieldErrors: new Map(),
  formErrors: [],
};

function pushErr(map: Map<string, string[]>, field: string, message: string): void {
  const existing = map.get(field);
  if (existing) existing.push(message);
  else map.set(field, [message]);
}

// ── Vitals ─────────────────────────────────────────────────────────────────
//
// Spec rules (per-section cross-field):
//   - bp_systolic set → bp_position required (currently in
//     validateCrossFields server-side, line 140-142).
//   - <vital>_not_taken === "yes" → <vital>_not_taken_reason required
//     AND <vital> value must be null. UI already enforces the second
//     half (renders the reason picker only when not_taken === yes,
//     hides the value input); we surface the reason-required gate.

export const VITAL_NOT_TAKEN_PAIRS: ReadonlyArray<{ flagField: keyof FormState; reasonField: keyof FormState; apiReasonField: keyof SubmitInput; label: string }> = [
  { flagField: "bp_not_taken",      reasonField: "bp_not_taken_reason",      apiReasonField: "bp_not_taken_reason",      label: "BP" },
  { flagField: "hr_not_taken",      reasonField: "hr_not_taken_reason",      apiReasonField: "hr_not_taken_reason",      label: "Heart rate" },
  { flagField: "spo2_not_taken",    reasonField: "spo2_not_taken_reason",    apiReasonField: "spo2_not_taken_reason",    label: "SpO2" },
  { flagField: "temp_not_taken",    reasonField: "temp_not_taken_reason",    apiReasonField: "temp_not_taken_reason",    label: "Temperature" },
  { flagField: "glucose_not_taken", reasonField: "glucose_not_taken_reason", apiReasonField: "glucose_not_taken_reason", label: "Glucose" },
  { flagField: "weight_not_taken",  reasonField: "weight_not_taken_reason",  apiReasonField: "weight_not_taken_reason",  label: "Weight" },
  // RR's API field is `resp_not_taken_reason` (server-side Zod uses
  // resp/_; the FormState uses rr_/_); other vitals' API field name
  // matches the FormState key. Compile-time `keyof SubmitInput` typing
  // closes the rr/resp drift bug class — typing "rr_not_taken_reason"
  // here would now fail tsc because the schema only knows "resp_*".
  { flagField: "rr_not_taken",      reasonField: "rr_not_taken_reason",      apiReasonField: "resp_not_taken_reason",    label: "Respiratory rate" },
];

export function validateVitals(f: FormState): SectionValidationResult {
  const fieldErrors = new Map<string, string[]>();
  const formErrors: string[] = [];

  if (f.bp_sys && !f.bp_pos) {
    pushErr(fieldErrors, "bp_position", "BP position is required when systolic is set");
  }
  for (const pair of VITAL_NOT_TAKEN_PAIRS) {
    if (f[pair.flagField] === "yes" && !f[pair.reasonField]) {
      pushErr(fieldErrors, pair.apiReasonField, `${pair.label}: select a reason for "not taken"`);
    }
  }
  return finalize(fieldErrors, formErrors);
}

// ── Mobility ───────────────────────────────────────────────────────────────
//
// Spec rules:
//   - fall_since === "yes" → fall_count_since_last_visit required, 0-10.
//   - fall_visit === "yes" OR fall_since === "yes" → fall_injury_sustained
//     not null (FormState init: "no", so always "no" or "yes"; UI
//     reveals the picker when either flag is yes).
//   - fall_injury_sustained === "yes" → fall_injury_description set.
//   - environment_safety_concern === true → home_condition_concern_type set.

function checkFallCount(f: FormState, errs: Map<string, string[]>): void {
  if (f.fall_since !== "yes") return;
  const n = Number(f.fall_count_since_last_visit);
  if (f.fall_count_since_last_visit === "" || Number.isNaN(n)) {
    pushErr(errs, "fall_count_since_last_visit", "Number of falls is required");
  } else if (n < 0 || n > 10) {
    pushErr(errs, "fall_count_since_last_visit", "Must be between 0 and 10");
  }
}

export function validateMobility(f: FormState): SectionValidationResult {
  const fieldErrors = new Map<string, string[]>();
  checkFallCount(f, fieldErrors);
  if (f.fall_injury_sustained === "yes" && !f.fall_injury_description) {
    pushErr(fieldErrors, "fall_injury_description", "Describe the injury");
  }
  if (f.environment_safety_concern && !f.home_condition_concern_type) {
    pushErr(fieldErrors, "home_condition_concern_type", "Select the type of home concern");
  }
  if (f.escalation && !f.escalation_severity) {
    pushErr(fieldErrors, "escalation_severity", "Escalation severity is required when escalation flag is set");
  }
  return finalize(fieldErrors, []);
}

// ── Clinical ───────────────────────────────────────────────────────────────
//
// Spec rules:
//   - overall_patient_status REQUIRED when Clinical visible (top-level
//     in submitReportSchema, line 98 — non-nullable enum).
//   - pain_present === "yes" → pain_severity ∈ [1,10] AND pain_location
//     set AND pain_vs_last_visit set.
//   - cognitive_test_used !== "not_done" && !== "unable_to_assess" →
//     cognitive_test_score integer in DRS range AND
//     cognitive_assessment_interpretation set.

function checkPainChain(f: FormState, errs: Map<string, string[]>): void {
  if (f.pain_present !== "yes") return;
  const sev = Number(f.pain_severity);
  if (f.pain_severity === "" || Number.isNaN(sev) || sev < 1 || sev > 10) {
    pushErr(errs, "pain_severity", "Pain severity 1-10 is required");
  }
  if (!f.pain_location) pushErr(errs, "pain_location", "Pain location is required");
  if (!f.pain_vs_last)  pushErr(errs, "pain_vs_last_visit", "Pain vs. last visit is required");
}

function checkCognitiveChain(f: FormState, errs: Map<string, string[]>): void {
  const tested = f.cog_test_used && f.cog_test_used !== "not_done" && f.cog_test_used !== "unable_to_assess";
  if (!tested) return;
  if (!f.cog_test_score) {
    pushErr(errs, "cognitive_test_score", "Cognitive test score is required when a test was used");
  }
  if (!f.cog_interpretation) {
    pushErr(errs, "cognitive_assessment_interpretation", "Interpretation is required when a test was used");
  }
}

export function validateClinical(f: FormState): SectionValidationResult {
  const fieldErrors = new Map<string, string[]>();
  if (!f.overall_status) {
    pushErr(fieldErrors, "overall_patient_status", "Overall patient status is required");
  }
  checkPainChain(f, fieldErrors);
  checkCognitiveChain(f, fieldErrors);
  return finalize(fieldErrors, []);
}

// ── Wellbeing ──────────────────────────────────────────────────────────────
//
// Spec rules:
//   - breathlessness_present === "yes" → breathlessness_severity AND
//     breathlessness_timing set.

export function validateWellbeing(f: FormState): SectionValidationResult {
  const fieldErrors = new Map<string, string[]>();
  const formErrors: string[] = [];

  if (f.breathlessness_present === "yes") {
    if (!f.breathlessness_severity) {
      pushErr(fieldErrors, "breathlessness_severity", "Breathlessness severity is required");
    }
    if (!f.breathlessness_timing) {
      pushErr(fieldErrors, "breathlessness_timing", "Breathlessness timing is required");
    }
  }
  return finalize(fieldErrors, formErrors);
}

// ── Medication ─────────────────────────────────────────────────────────────
//
// Spec rules:
//   - Each adherence_log entry must have drug_name non-empty AND
//     adherence_status set. The wizard pre-seeds one empty row; we
//     ignore fully-empty rows (treated as "not added") but flag a row
//     with one of the two fields filled and the other missing.

function checkMedRow(
  row: { drug_name: string; adherence_status: string }, index: number,
  errs: Map<string, string[]>,
): void {
  const drugSet = row.drug_name.trim() !== "";
  // Empty drug_name → carer hasn't entered this row; skip (adherence_status
  // defaults to "compliant_all_doses" in INIT, but a row with no drug name
  // is filtered out of the submit payload by buildPayload — counts as zero
  // meds, not an incomplete row).
  if (!drugSet) return;
  if (!row.adherence_status) {
    pushErr(errs, `medication_adherence_log.${index}.adherence_status`,
      `Medication ${index + 1}: adherence is required`);
  }
}

export function validateMedication(f: FormState): SectionValidationResult {
  const fieldErrors = new Map<string, string[]>();
  for (let i = 0; i < f.meds.length; i++) {
    const m = f.meds[i];
    if (m) checkMedRow(m, i, fieldErrors);
  }
  return finalize(fieldErrors, []);
}

// ── Notes ──────────────────────────────────────────────────────────────────
//
// Per spec: "no per-section gate; cross-section at submit". The 1000-
// char client cap matches the server schema as of TASK-46 closure
// (S6 — supplementary_notes max 1000).

const NOTES_MAX = 1000;

export function validateNotes(f: FormState): SectionValidationResult {
  const fieldErrors = new Map<string, string[]>();
  const formErrors: string[] = [];
  if (f.notes.length > NOTES_MAX) {
    pushErr(fieldErrors, "supplementary_notes", `Notes must be ${NOTES_MAX} characters or fewer`);
  }
  return finalize(fieldErrors, formErrors);
}

// ── Dispatcher ─────────────────────────────────────────────────────────────

const SECTION_VALIDATORS: Record<SectionStep, (f: FormState) => SectionValidationResult> = {
  vitals:     validateVitals,
  mobility:   validateMobility,
  clinical:   validateClinical,
  wellbeing:  validateWellbeing,
  medication: validateMedication,
  notes:      validateNotes,
};

export function validateSection(section: SectionStep, f: FormState): SectionValidationResult {
  return SECTION_VALIDATORS[section]?.(f) ?? EMPTY_RESULT;
}

function finalize(
  fieldErrors: Map<string, string[]>,
  formErrors:  string[],
): SectionValidationResult {
  return {
    isComplete: fieldErrors.size === 0 && formErrors.length === 0,
    fieldErrors,
    formErrors,
  };
}
